import { useState, useCallback } from "react";
import { apiRequest } from "../api/baseApi";

/**
 * Custom hook for force logout functionality
 * Handles state, API calls, and auto-dismiss of success messages
 *
 * @param {string|number} clientId - The ID of the client to force logout
 * @returns {Object} Force logout state and actions
 */
export const useForceLogout = (clientId) => {
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const openModal = useCallback(() => {
    setError(null);
    setShowModal(true);
  }, []);

  const closeModal = useCallback(() => {
    if (!isLoading) {
      setShowModal(false);
      setError(null);
    }
  }, [isLoading]);

  const clearResult = useCallback(() => {
    setResult(null);
  }, []);

  const forceLogout = useCallback(async () => {
    if (!clientId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiRequest(`clients/${clientId}/force-logout`, {
        method: "POST",
        version: "v2",
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setResult(data);
        setShowModal(false);
        // Auto-hide success message after 8 seconds
        setTimeout(() => setResult(null), 8000);
      } else {
        setError(data.message || "Failed to force logout client");
      }
    } catch (err) {
      setError(err.message || "An error occurred while forcing logout");
    } finally {
      setIsLoading(false);
    }
  }, [clientId]);

  return {
    // State
    showModal,
    isLoading,
    error,
    result,
    // Actions
    openModal,
    closeModal,
    forceLogout,
    clearResult,
  };
};

export default useForceLogout;

